import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  CreditCard,
  Building2,
  Check,
  AlertCircle,
  Loader2,
  Lock,
  FileText,
  Info
} from 'lucide-react';
import lawpayService from '../services/lawpayService';

const PublicPayment = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [transactionId, setTransactionId] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    exp_month: '',
    exp_year: '',
    postal_code: ''
  });

  // Hosted fields state
  const [hostedFieldsLoaded, setHostedFieldsLoaded] = useState(false);
  const [hostedFieldsReady, setHostedFieldsReady] = useState(false);
  const hostedFieldsRef = useRef(null);

  // Surcharge state
  const [surchargeable, setSurchargeable] = useState(null); // null = unknown, true = credit, false = debit/prepaid
  const [surchargeInfo, setSurchargeInfo] = useState(null); // { surcharge_amount, total_amount, ... }
  const [surchargeLoading, setSurchargeLoading] = useState(false);
  const [totalCharged, setTotalCharged] = useState(null); // total amount actually charged (for success screen)

  // Load payment link info
  useEffect(() => {
    const loadPaymentInfo = async () => {
      try {
        const response = await lawpayService.getPublicPaymentLink(token);
        setPaymentInfo(response.data);

        // Pre-fill form with client info if available
        if (response.data.client_email) {
          setFormData(prev => ({ ...prev, email: response.data.client_email }));
        }
        if (response.data.client_name) {
          setFormData(prev => ({ ...prev, name: response.data.client_name }));
        }
      } catch (err) {
        console.error('Error loading payment info:', err);
        if (err.response?.status === 404) {
          setError('This payment link was not found or has expired.');
        } else if (err.response?.status === 400) {
          setError(err.response.data.detail || 'This payment link is no longer active.');
        } else {
          setError('Failed to load payment information. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadPaymentInfo();
  }, [token]);

  // Fetch surcharge info when card is detected as surchargeable
  const fetchSurchargeInfo = useCallback(async () => {
    if (!paymentInfo?.surcharge_enabled || !paymentInfo?.bank_account_id) return;

    setSurchargeLoading(true);
    try {
      const amountCents = Math.round(paymentInfo.amount * 100);
      const response = await lawpayService.getSurchargeInfo(token, amountCents);
      setSurchargeInfo(response.data);
    } catch (err) {
      console.error('Error fetching surcharge info:', err);
      // Don't block payment if surcharge lookup fails - just don't show surcharge
      setSurchargeInfo(null);
    } finally {
      setSurchargeLoading(false);
    }
  }, [token, paymentInfo?.surcharge_enabled, paymentInfo?.bank_account_id, paymentInfo?.amount]);

  useEffect(() => {
    if (surchargeable === true) {
      fetchSurchargeInfo();
    } else {
      setSurchargeInfo(null);
    }
  }, [surchargeable, fetchSurchargeInfo]);

  // Load LawPay/8am hosted fields script
  useEffect(() => {
    if (!paymentInfo?.public_key || hostedFieldsLoaded) return;

    const script = document.createElement('script');
    // Official 8am/AffiniPay hosted fields SDK
    script.src = 'https://cdn.affinipay.com/hostedfields/1.5.3/fieldGen_1.5.3.js';
    script.async = true;
    script.onload = () => {
      setHostedFieldsLoaded(true);
    };
    script.onerror = () => {
      setError('Failed to load payment form. Please refresh the page.');
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [paymentInfo?.public_key, hostedFieldsLoaded]);

  // Initialize hosted fields
  const initializeHostedFields = useCallback(() => {
    if (!hostedFieldsLoaded || !paymentInfo?.public_key || hostedFieldsRef.current) return;

    // Check if the AffiniPay SDK is loaded
    if (!window.AffiniPay || !window.AffiniPay.HostedFields) {
      console.error('AffiniPay SDK not loaded');
      setError('Failed to initialize payment form. Please refresh the page.');
      return;
    }

    try {
      const config = {
        publicKey: paymentInfo.public_key,
        fields: [
          {
            selector: '#card-number',
            input: {
              type: 'credit_card_number',
              css: {
                'font-family': 'system-ui, -apple-system, sans-serif',
                'font-size': '16px',
                'color': '#1f2937'
              }
            }
          },
          {
            selector: '#card-cvv',
            input: {
              type: 'cvv',
              css: {
                'font-family': 'system-ui, -apple-system, sans-serif',
                'font-size': '16px',
                'color': '#1f2937'
              }
            }
          }
        ]
      };

      // Enable surcharge detection if firm has it enabled
      if (paymentInfo.surcharge_enabled) {
        config.surchargeEnabled = true;
      }

      // initializeFields returns the hostedFields instance
      // The callback receives state updates (not the instance)
      const hostedFields = window.AffiniPay.HostedFields.initializeFields(config, (state) => {
        // State callback - called when field state changes
        console.log('Hosted fields state update:', state);

        // Check for surchargeable status from SDK
        if (paymentInfo.surcharge_enabled && state.surchargeable !== undefined) {
          setSurchargeable(state.surchargeable);
        }
      });

      if (hostedFields) {
        hostedFieldsRef.current = hostedFields;
        setHostedFieldsReady(true);
      } else {
        setError('Failed to initialize payment form. Please check your configuration.');
      }
    } catch (err) {
      console.error('Error initializing hosted fields:', err);
      setError('Failed to initialize payment form: ' + (err.message || 'Unknown error'));
    }
  }, [hostedFieldsLoaded, paymentInfo?.public_key, paymentInfo?.surcharge_enabled]);

  useEffect(() => {
    if (hostedFieldsLoaded && paymentInfo?.public_key) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(initializeHostedFields, 100);
      return () => clearTimeout(timer);
    }
  }, [hostedFieldsLoaded, paymentInfo?.public_key, initializeHostedFields]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatCents = (cents) => {
    return formatCurrency(cents / 100);
  };

  // Compute surcharge display values
  const surchargeAmountCents = (surchargeable && surchargeInfo?.surcharge_amount) ? surchargeInfo.surcharge_amount : 0;
  const baseAmountCents = paymentInfo ? Math.round(paymentInfo.amount * 100) : 0;
  const totalAmountCents = baseAmountCents + surchargeAmountCents;
  const showSurcharge = paymentInfo?.surcharge_enabled && surchargeable === true && surchargeAmountCents > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    if (!formData.exp_month || !formData.exp_year) {
      setError('Please enter the card expiration date');
      return;
    }

    if (!hostedFieldsRef.current) {
      setError('Payment form not ready. Please wait or refresh the page.');
      return;
    }

    setProcessing(true);

    try {
      // Tokenize card with hosted fields
      const tokenResult = await new Promise((resolve, reject) => {
        const state = hostedFieldsRef.current.getState();
        console.log('Hosted fields state:', JSON.stringify(state, null, 2));

        // Check individual field validity instead of overall isValid
        const cardNumberField = state.fields?.find(f => f.type === 'credit_card_number');
        const cvvField = state.fields?.find(f => f.type === 'cvv');

        console.log('Card number field:', cardNumberField);
        console.log('CVV field:', cvvField);

        // Get payment token - include expiration and address data
        hostedFieldsRef.current.getPaymentToken({
          name: formData.name,
          exp_month: formData.exp_month,
          exp_year: formData.exp_year,
          postal_code: formData.postal_code
        })
          .then(result => {
            console.log('Token result (full):', JSON.stringify(result, null, 2));
            console.log('Token ID:', result.id);
            if (result.error) {
              reject(new Error(result.error.message || 'Failed to process card'));
            } else if (!result.id) {
              // If no ID, maybe the result itself is the token data to use
              reject(new Error('No payment token ID received'));
            } else {
              resolve(result);
            }
          })
          .catch(err => {
            console.error('getPaymentToken error:', err);
            reject(new Error(err.message || 'Failed to process card'));
          });
      });

      console.log('Sending to backend with token ID:', tokenResult.id);

      // Build payment request
      const paymentData = {
        payment_token: tokenResult.id,
        email: formData.email,
        name: formData.name
      };

      // Include surcharge if applicable
      if (showSurcharge) {
        paymentData.surcharge_amount = surchargeAmountCents;
      }

      // Process payment with our backend
      const response = await lawpayService.processPublicPayment(token, paymentData);

      if (response.data.success) {
        setPaymentSuccess(true);
        setTransactionId(response.data.transaction_id);
        setTotalCharged(showSurcharge ? totalAmountCents : null);
      } else {
        setError(response.data.message || 'Payment failed');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            <span className="ml-3 text-gray-600">Loading payment details...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state (link not found or expired)
  if (error && !paymentInfo) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Payment Link Unavailable</h1>
            <p className="text-gray-600">{error}</p>
            <p className="text-sm text-gray-500 mt-4">
              Please contact the law firm if you believe this is an error.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
            {totalCharged ? (
              <div className="text-gray-600 mb-4">
                <p>Your card has been charged {formatCents(totalCharged)}.</p>
                <div className="mt-2 text-sm text-gray-500">
                  <p>Subtotal: {formatCurrency(paymentInfo.amount)}</p>
                  <p>Card surcharge: {formatCents(totalCharged - Math.round(paymentInfo.amount * 100))}</p>
                </div>
              </div>
            ) : (
              <p className="text-gray-600 mb-4">
                Your payment of {formatCurrency(paymentInfo.amount)} has been processed.
              </p>
            )}
            {transactionId && (
              <p className="text-sm text-gray-500 mb-4">
                Transaction ID: {transactionId}
              </p>
            )}
            <p className="text-sm text-gray-600">
              A receipt has been sent to {formData.email}
            </p>
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-400">
                Paid to {paymentInfo.firm_name}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Payment form
  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 overflow-x-hidden">
      <style>{`
        #card-number iframe, #card-cvv iframe {
          width: 100% !important;
          height: 100% !important;
          max-height: 42px !important;
        }
      `}</style>
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <Building2 className="w-12 h-12 text-gray-700 mx-auto mb-2" />
          <h1 className="text-xl font-semibold text-gray-900">{paymentInfo.firm_name}</h1>
        </div>

        {/* Payment Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Amount Section */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-6">
            <div className="text-center">
              {showSurcharge ? (
                <>
                  <p className="text-primary-100 text-sm mb-1">Subtotal</p>
                  <p className="text-2xl font-bold">{formatCurrency(paymentInfo.amount)}</p>
                  <div className="mt-2 text-primary-100 text-sm">
                    <p>Card surcharge: +{formatCents(surchargeAmountCents)}</p>
                  </div>
                  <div className="mt-2 pt-2 border-t border-primary-400">
                    <p className="text-primary-100 text-sm">Total</p>
                    <p className="text-3xl font-bold">{formatCents(totalAmountCents)}</p>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-primary-100 text-sm mb-1">Amount Due</p>
                  <p className="text-4xl font-bold">{formatCurrency(paymentInfo.amount)}</p>
                </>
              )}
              {paymentInfo.invoice_number && (
                <p className="text-primary-100 text-sm mt-2 flex items-center justify-center">
                  <FileText className="w-4 h-4 mr-1" />
                  Invoice: {paymentInfo.invoice_number}
                </p>
              )}
            </div>
          </div>

          {/* Surcharge notice for debit/prepaid */}
          {paymentInfo.surcharge_enabled && surchargeable === false && (
            <div className="px-6 pt-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center text-green-800 text-sm">
                  <Info className="w-4 h-4 mr-2 flex-shrink-0" />
                  No surcharge for debit cards. You will only be charged {formatCurrency(paymentInfo.amount)}.
                </div>
              </div>
            </div>
          )}

          {/* Surcharge loading indicator */}
          {paymentInfo.surcharge_enabled && surchargeable === true && surchargeLoading && (
            <div className="px-6 pt-4">
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
                <div className="flex items-center text-primary-800 text-sm">
                  <Loader2 className="w-4 h-4 mr-2 flex-shrink-0 animate-spin" />
                  Calculating surcharge...
                </div>
              </div>
            </div>
          )}

          {/* Details Section */}
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <p className="text-gray-700">{paymentInfo.description}</p>
            {paymentInfo.client_name && (
              <p className="text-sm text-gray-500 mt-2">
                For: {paymentInfo.client_name}
              </p>
            )}
            <p className="text-xs text-gray-400 mt-2">
              Depositing to: {paymentInfo.account_type === 'trust' ? 'Trust Account' : 'Operating Account'}
            </p>
          </div>

          {/* Payment Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="your@email.com"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Receipt will be sent to this email</p>
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name on Card
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="John Smith"
              />
            </div>

            {/* Card Number - Hosted Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <CreditCard className="w-4 h-4 inline mr-1" />
                Card Number *
              </label>
              <div
                id="card-number"
                style={{ height: 42, overflow: 'hidden' }}
                className="block w-full border border-gray-300 rounded-md bg-white"
              />
            </div>

            {/* Expiry and CVV */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Month *
                </label>
                <select
                  value={formData.exp_month}
                  onChange={(e) => setFormData(prev => ({ ...prev, exp_month: e.target.value }))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">MM</option>
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                      {String(i + 1).padStart(2, '0')}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year *
                </label>
                <select
                  value={formData.exp_year}
                  onChange={(e) => setFormData(prev => ({ ...prev, exp_year: e.target.value }))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">YY</option>
                  {[...Array(15)].map((_, i) => {
                    const year = new Date().getFullYear() + i;
                    return (
                      <option key={year} value={String(year)}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CVV *
                </label>
                <div
                  id="card-cvv"
                  style={{ height: 42, overflow: 'hidden' }}
                  className="block w-full border border-gray-300 rounded-md bg-white"
                />
              </div>
            </div>

            {/* Zip Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Billing Zip Code *
              </label>
              <input
                type="text"
                value={formData.postal_code}
                onChange={(e) => setFormData(prev => ({ ...prev, postal_code: e.target.value.replace(/[^0-9-]/g, '').slice(0, 10) }))}
                className="block w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="12345"
                required
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center text-red-800">
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={processing || !hostedFieldsReady}
              className="w-full py-3 px-4 bg-primary-600 text-white rounded-md font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {processing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Processing...
                </>
              ) : !hostedFieldsReady ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Loading...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5 mr-2" />
                  Pay {showSurcharge ? formatCents(totalAmountCents) : formatCurrency(paymentInfo.amount)}
                </>
              )}
            </button>

            {/* Security Note */}
            <div className="text-center text-xs text-gray-500 flex items-center justify-center">
              <Lock className="w-3 h-3 mr-1" />
              Secured by LawPay. Your card details are encrypted.
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-xs text-gray-400">
          <p>Powered by LawPay</p>
        </div>
      </div>
    </div>
  );
};

export default PublicPayment;
